/**
 * Force Momentum (fM) — a configurable Elder Force Index family.
 *
 *   length      smoothing/baseline period (e.g. 13)
 *   mode        0 Traditional Elder · 1 Volume-neutralized · 2 Price-only
 *   multiplier  display scale (default 100, matching CUE's "fM" magnitude)
 *
 * Per-bar raw value, then EMA(length), then × multiplier:
 *   mode 0  raw_i = Volume_i × (Close_i − Close_{i-1})                       (classic Elder)
 *   mode 1  raw_i = (Close_i − Close_{i-1}) × Volume_i / EMA(Volume,length)_i (volume-neutralized)
 *   mode 2  raw_i = (Close_i − Close_{i-1})                                  (price-only)
 *
 * computeSeries is index-aligned to `closes` (bar 0 is null — no Δclose), so
 * the chart plots the whole series and the scanner/watchlist take the last
 * finite value. Mode 1 at length 2/5/13 × 100 reproduces the scanner's
 * fM2/fM5/fM13 exactly.
 */
import { familyGroup } from "./indicatorGroups";
import { emaSeries } from "./ema";
import type { OHLCV, IndicatorParams, SharedIndicatorDef } from "./types";

export const FORCE_MOMENTUM: SharedIndicatorDef = {
  id: "fm",
  label: "Afiat - Force Momentum",
  group: familyGroup("fm"),
  signColored: true,
  format: "signed",
  referenceLevels: [0],
  params: [
    { id: "length", label: "Length", kind: "number", default: 13, min: 1, max: 200, step: 1 },
    {
      id: "mode",
      label: "Mode",
      kind: "select",
      default: 1,
      choices: [
        { value: 0, label: "Traditional (Elder)" },
        { value: 1, label: "Volume-neutralized" },
        { value: 2, label: "Price-only" },
      ],
    },
    { id: "multiplier", label: "Multiplier", kind: "number", default: 100, min: 0, step: 1 },
  ],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes, volumes } = data;
    const n = closes.length;
    const out: (number | null)[] = new Array(n).fill(null);
    if (n < 2) return out;

    const length = Math.max(1, Math.round(params.length ?? 13));
    const mode = Math.round(params.mode ?? 1);
    const mult = params.multiplier ?? 100;

    // Per-bar raw force series (covers bars 1..n-1).
    const volEma = mode === 1 ? emaSeries(volumes, length) : null;
    const raw: number[] = [];
    for (let i = 1; i < n; i++) {
      const dc = closes[i]! - closes[i - 1]!;
      let r: number;
      if (mode === 0) {
        r = volumes[i]! * dc;
      } else if (mode === 1) {
        const base = volEma![i]!;
        const relVol = base > 0 ? volumes[i]! / base : 0;
        r = dc * relVol;
      } else {
        r = dc;
      }
      raw.push(r);
    }

    const smoothed = emaSeries(raw, length); // aligned to bars 1..n-1
    for (let i = 1; i < n; i++) {
      const v = smoothed[i - 1]!;
      out[i] = Number.isFinite(v) ? v * mult : null;
    }
    return out;
  },
};
